import React, { useRef } from 'react';
import Box from '@mui/material/Box';
import Dialog from '@mui/material/Dialog';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import Typography from '@mui/material/Typography';
import { useReactToPrint } from 'react-to-print';
import BackIcon from '../../common/Back/BackIcon';
import PageHeading from '../../common/Heading/PageHeading';
import { ContentTitle } from '../../ui/styled';
import Grid from '@mui/material/Grid';
import { LeftGrid } from '../../Episode/Episode.Styled';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';

const printStyles = `
  @media print {
    @page {
      margin: 20mm;
      @bottom-right {
        content: "Page " counter(page) " of " counter(pages);
      }
    }

    /* Force print color adjustment */
    * {
      -webkit-print-color-adjust: exact !important;
      print-color-adjust: exact !important;
      color-adjust: exact !important;
    }

    .print-footer {
      position: fixed;
      bottom: 0;
      right: 0;
      font-size: 10pt;
      padding: 10px;
    }

    .print-footer::after {
      content: counter(page);
    }

    .page-break {
      page-break-before: always !important;
      break-before: page !important;
      display: block !important;
      clear: both !important;
    }

    .MuiPaper-root {
      page-break-inside: avoid !important;
      break-inside: avoid !important;
      box-shadow: none !important;
      border: 1px solid #ddd !important;
    }

    .MuiTableContainer-root {
      page-break-inside: auto !important;
    }

    /* Preserve table styling */
    table {
      border-collapse: collapse !important;
    }

    th, td {
      border: 1px solid #ddd !important;
    }

    /* Preserve background colors */
    .MuiTableHead-root .MuiTableRow-root {
      background-color: #747070 !important;
    }

    .MuiTableHead-root .MuiTableCell-root {
      color: #fff !important;
      background-color: #747070 !important;
    }

    .MuiTableRow-root:nth-of-type(odd) {
      background-color: #eee !important;
    }

    .MuiTableRow-root[style*="backgroundColor: rgb(208, 208, 208)"],
    .MuiTableRow-root[style*="backgroundColor:#d0d0d0"] {
      background-color: #d0d0d0 !important;
    }

    .MuiTableRow-root[style*="backgroundColor: rgb(224, 224, 224)"],
    .MuiTableRow-root[style*="backgroundColor:#e0e0e0"] {
      background-color: #e0e0e0 !important;
    }
  }
`;

const CreditedTreatmentsPrintDialog = (props) => {
  const STOP_REGION = process.env.REACT_APP_STOP_REGION;
  const { invoiceMonth, treatments, openDialog, setOpenDialog } = props;
  const componentRef = useRef();

  const handlePrint = useReactToPrint({
    content: () => componentRef.current,
    pageStyle: printStyles,
  });

  const handleClose = () => {
    setOpenDialog(false);
  };

  // Helper function to add ordinal suffix to day numbers
  const addOrdinalSuffix = (day) => {
    if (day >= 11 && day <= 13) {
      return day + 'th';
    }
    switch (day % 10) {
      case 1: return day + 'st';
      case 2: return day + 'nd';
      case 3: return day + 'rd';
      default: return day + 'th';
    }
  };

  // Helper functions for Individual treatments
  const calculateIndividualTotal = (treatment) => {
    let total = 0;
    for (let i = 1; i <= 31; i++) {
      total += treatment[`iAmt${i}`] || 0;
    }
    return total;
  };

  const getIndividualDays = (treatment) => {
    const days = [];
    for (let i = 1; i <= 31; i++) {
      if (treatment[`i${i}`]) {
        days.push(addOrdinalSuffix(i));
      }
    }
    return days.length > 0 ? days.join(', ') : 'None';
  };

  const countIndividualUnits = (treatment) => {
    let count = 0;
    for (let i = 1; i <= 31; i++) {
      if (treatment[`i${i}`]) {
        count++;
      }
    }
    return count;
  };

  const hasIndividualTreatments = (treatment) => {
    for (let i = 1; i <= 31; i++) {
      if (treatment[`i${i}`]) {
        return true;
      }
    }
    return false;
  };

  // Helper functions for Group treatments
  const calculateGroupTotal = (treatment) => {
    let total = 0;
    for (let i = 1; i <= 31; i++) {
      total += treatment[`gAmt${i}`] || 0;
    }
    return total;
  };

  const getGroupDays = (treatment) => {
    const days = [];
    for (let i = 1; i <= 31; i++) {
      if (treatment[`g${i}`]) {
        days.push(addOrdinalSuffix(i));
      }
    }
    return days.length > 0 ? days.join(', ') : 'None';
  };

  const countGroupUnits = (treatment) => {
    let count = 0;
    for (let i = 1; i <= 31; i++) {
      if (treatment[`g${i}`]) {
        count++;
      }
    }
    return count;
  };

  const hasGroupTreatments = (treatment) => {
    for (let i = 1; i <= 31; i++) {
      if (treatment[`g${i}`]) {
        return true;
      }
    }
    return false;
  };

  let grandTotal = 0;

  return (
    <Dialog
      fullScreen
      open={openDialog}
      onClose={setOpenDialog}
    >
      <ContentTitle container>
        <Grid item xs={10}>
          <Box style={{ display: 'flex', alignItems: 'center' }}>
            <BackIcon />
            <PageHeading title='Credited Treatments Report' />
          </Box>
        </Grid>
        <LeftGrid item xs={2} pr={4} pt={1}>
          <Button variant="contained" onClick={() => handlePrint()}>
            Print
          </Button>
          {/*<IconButton onClick={handleClose}>
            <CloseIcon />
          </IconButton>*/}
        </LeftGrid>
      </ContentTitle>

      <Box p={4} ref={componentRef}>
        {
          Object.keys(treatments).sort().map((mthID, index) => {
            let monthTotal = 0;

            return (
              <div key={index} className={index > 0 ? 'page-break' : ''}>
                <Typography variant="h5" align="center">Westcare of California STOP Area {STOP_REGION}</Typography>
                <Typography variant="h4" align="center">Credited Treatments Report</Typography>
                <Typography variant="h5" align="center">Invoice Month: {invoiceMonth}</Typography>
                <Box pt={2} />

                <Typography variant="h6" sx={{backgroundColor: '#747070', color: '#fff', padding: '8px', marginBottom: '12px'}}>
                  Credited Treatments Report for the Month of {mthID}
                </Typography>

                {
                  Object.keys(treatments[mthID]).sort().map((cboName, cbpIdx) => {
                    let cbpTotal = 0;

                    return (
                      <Box key={cbpIdx} sx={{marginBottom: '20px'}}>
                        <Typography variant="subtitle1" sx={{backgroundColor: '#d0d0d0', fontWeight: 'bold', padding: '6px', marginBottom: '0px'}}>
                          {cboName}
                        </Typography>

                        <TableContainer component={Paper}>
                          <Table sx={{ minWidth: 650 }} aria-label="simple table">
                            <TableHead>
                              <TableRow sx={{backgroundColor: '#747070'}}>
                                <TableCell align={'center'} sx={{color: '#fff', padding: '4px', fontSize: '12px'}}>CDC#</TableCell>
                                <TableCell align={'center'} sx={{color: '#fff', padding: '4px', fontSize: '12px'}}>Participant</TableCell>
                                <TableCell align={'center'} sx={{color: '#fff', padding: '4px', fontSize: '12px'}}>Modality</TableCell>
                                <TableCell align={'center'} sx={{color: '#fff', padding: '4px', fontSize: '12px'}}>Type</TableCell>
                                <TableCell align={'center'} sx={{color: '#fff', padding: '4px', fontSize: '12px'}}>Treatment Days</TableCell>
                                <TableCell align={'center'} sx={{color: '#fff', padding: '4px', fontSize: '12px'}}>Units</TableCell>
                                <TableCell align={'center'} sx={{color: '#fff', padding: '4px', fontSize: '12px'}}>Rate</TableCell>
                                <TableCell align={'center'} sx={{color: '#fff', padding: '4px', fontSize: '12px'}}>Treatment Total</TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                          {
                            treatments[mthID][cboName].map((treatment, idx) => {
                              // Calculate total once for accumulation
                              const treatmentTotal = calculateIndividualTotal(treatment) + calculateGroupTotal(treatment);
                              cbpTotal += treatmentTotal;
                              monthTotal += treatmentTotal;
                              grandTotal += treatmentTotal;

                              const rows = [];

                              // Render Individual treatment row if exists
                              if (hasIndividualTreatments(treatment)) {
                                const individualTotal = calculateIndividualTotal(treatment);
                                const individualUnits = countIndividualUnits(treatment);
                                const individualRate = individualUnits > 0 ? individualTotal / individualUnits : 0;

                                rows.push(
                                  <TableRow
                                    key={`${idx}-individual`}
                                    sx={{ '&:nth-of-type(odd)': {backgroundColor: '#eee'}, '&:last-child td, &:last-child th': { border: 0} }}
                                  >
                                    <TableCell align={'center'} sx={{padding: '4px', fontSize: '11px'}}>
                                      <Typography sx={{padding: '0px', fontSize: '11px'}}>{treatment.epdIcnCDC}</Typography>
                                    </TableCell>
                                    <TableCell align={'left'} sx={{padding: '4px', fontSize: '11px'}}>
                                      <Typography sx={{padding: '0px', fontSize: '11px'}}>{treatment.prtKey}</Typography>
                                    </TableCell>
                                    <TableCell align={'center'} sx={{padding: '4px', fontSize: '11px'}}>
                                      <Typography sx={{padding: '0px', fontSize: '11px'}}>{treatment.cbo_ModID}</Typography>
                                    </TableCell>
                                    <TableCell align={'center'} sx={{padding: '4px', fontSize: '11px'}}>
                                      <Typography sx={{padding: '0px', fontSize: '11px'}}>Individual</Typography>
                                    </TableCell>
                                    <TableCell align={'center'} sx={{padding: '4px', fontSize: '11px'}}>
                                      <Typography sx={{padding: '0px', fontSize: '11px'}}>{getIndividualDays(treatment)}</Typography>
                                    </TableCell>
                                    <TableCell align={'center'} sx={{padding: '4px', fontSize: '11px'}}>
                                      <Typography sx={{padding: '0px', fontSize: '11px'}}>
                                        {individualUnits}
                                      </Typography>
                                    </TableCell>
                                    <TableCell align={'right'} sx={{padding: '4px', fontSize: '11px'}}>
                                      <Typography sx={{padding: '0px', fontSize: '11px'}}>
                                        {individualRate.toLocaleString('en-US', {style: 'currency', currency: 'USD'})}
                                      </Typography>
                                    </TableCell>
                                    <TableCell align={'right'} sx={{padding: '4px', fontSize: '11px'}}>
                                      <Typography sx={{padding: '0px', fontSize: '11px'}}>
                                        {individualTotal.toLocaleString('en-US', {style: 'currency', currency: 'USD'})}
                                      </Typography>
                                    </TableCell>
                                  </TableRow>
                                );
                              }

                              // Render Group treatment row if exists
                              if (hasGroupTreatments(treatment)) {
                                const groupTotal = calculateGroupTotal(treatment);
                                const groupUnits = countGroupUnits(treatment);
                                const groupRate = groupUnits > 0 ? groupTotal / groupUnits : 0;

                                rows.push(
                                  <TableRow
                                    key={`${idx}-group`}
                                    sx={{ '&:nth-of-type(odd)': {backgroundColor: '#eee'}, '&:last-child td, &:last-child th': { border: 0} }}
                                  >
                                    <TableCell align={'center'} sx={{padding: '4px', fontSize: '11px'}}>
                                      <Typography sx={{padding: '0px', fontSize: '11px'}}>{treatment.epdIcnCDC}</Typography>
                                    </TableCell>
                                    <TableCell align={'left'} sx={{padding: '4px', fontSize: '11px'}}>
                                      <Typography sx={{padding: '0px', fontSize: '11px'}}>{treatment.prtKey}</Typography>
                                    </TableCell>
                                    <TableCell align={'center'} sx={{padding: '4px', fontSize: '11px'}}>
                                      <Typography sx={{padding: '0px', fontSize: '11px'}}>{treatment.cbo_ModID}</Typography>
                                    </TableCell>
                                    <TableCell align={'center'} sx={{padding: '4px', fontSize: '11px'}}>
                                      <Typography sx={{padding: '0px', fontSize: '11px'}}>Group</Typography>
                                    </TableCell>
                                    <TableCell align={'center'} sx={{padding: '4px', fontSize: '11px'}}>
                                      <Typography sx={{padding: '0px', fontSize: '11px'}}>{getGroupDays(treatment)}</Typography>
                                    </TableCell>
                                    <TableCell align={'center'} sx={{padding: '4px', fontSize: '11px'}}>
                                      <Typography sx={{padding: '0px', fontSize: '11px'}}>
                                        {groupUnits}
                                      </Typography>
                                    </TableCell>
                                    <TableCell align={'right'} sx={{padding: '4px', fontSize: '11px'}}>
                                      <Typography sx={{padding: '0px', fontSize: '11px'}}>
                                        {groupRate.toLocaleString('en-US', {style: 'currency', currency: 'USD'})}
                                      </Typography>
                                    </TableCell>
                                    <TableCell align={'right'} sx={{padding: '4px', fontSize: '11px'}}>
                                      <Typography sx={{padding: '0px', fontSize: '11px'}}>
                                        {groupTotal.toLocaleString('en-US', {style: 'currency', currency: 'USD'})}
                                      </Typography>
                                    </TableCell>
                                  </TableRow>
                                );
                              }

                              return rows;
                            })
                          }
                              <TableRow
                                sx={{backgroundColor: '#e0e0e0'}}
                              >
                                <TableCell colSpan={8} align={'right'} sx={{fontWeight: 'bold', padding: '4px', fontSize: '12px'}}>
                                  Subtotal for {cboName}: {cbpTotal.toLocaleString('en-US', {style: 'currency', currency: 'USD'})}
                                </TableCell>
                              </TableRow>
                            </TableBody>
                          </Table>
                        </TableContainer>
                      </Box>
                    );
                  })
                }

                <TableContainer component={Paper} sx={{marginTop: '20px'}}>
                  <Table sx={{ minWidth: 650 }} aria-label="simple table">
                      <TableHead>
                      <TableRow
                        sx={{backgroundColor: '#747070'}}
                      >
                        <TableCell colSpan={8} align={'right'} sx={{color: '#fff', padding: '8px', fontSize: '14px'}}>
                          Total for {mthID}: {monthTotal.toLocaleString('en-US', {style: 'currency', currency: 'USD'})}
                        </TableCell>
                      </TableRow>
                      </TableHead>
                  </Table>
                </TableContainer>

              {/* Signature Lines */}
              <Box sx={{ marginTop: '40px', marginBottom: '20px' }}>
                <Grid container spacing={4}>
                  <Grid item xs={12}>
                    <Typography variant="body2" sx={{ fontWeight: 'bold', marginBottom: '10px' }}>
                      CBP Approval
                    </Typography>
                    <Grid container spacing={2}>
                      <Grid item xs={6}>
                        <Typography variant="body2">
                          Approved By: _________________________________
                        </Typography>
                      </Grid>
                      <Grid item xs={6}>
                        <Typography variant="body2">
                          Approval Date: _________________________________
                        </Typography>
                      </Grid>
                    </Grid>
                  </Grid>

                  <Grid item xs={12}>
                    <Typography variant="body2" sx={{ fontWeight: 'bold', marginBottom: '10px' }}>
                      SASCA Approval
                    </Typography>
                    <Grid container spacing={2}>
                      <Grid item xs={6}>
                        <Typography variant="body2">
                          Approved By: _________________________________
                        </Typography>
                      </Grid>
                      <Grid item xs={6}>
                        <Typography variant="body2">
                          Approval Date: _________________________________
                        </Typography>
                      </Grid>
                    </Grid>
                  </Grid>
                </Grid>
              </Box>

              </div>
            );
          })
        }

        {Object.keys(treatments).length > 1 && (
          <TableContainer component={Paper} sx={{paddingTop: '12px'}}>
            <Table sx={{ minWidth: 650 }} aria-label="simple table">
              <TableHead>
                <TableRow
                  sx={{backgroundColor: '#747070'}}
                >
                  <TableCell colSpan={8} align={'right'} sx={{color: '#fff', padding: '8px', fontSize: '16px'}}>
                    Grand Total: {grandTotal.toLocaleString('en-US', {style: 'currency', currency: 'USD'})}
                  </TableCell>
                </TableRow>
              </TableHead>
            </Table>
          </TableContainer>
        )}

        <div className="print-footer" style={{ display: 'none' }}>
          Page
        </div>
      </Box>
    </Dialog>
  );
};

export default CreditedTreatmentsPrintDialog;
